import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import Badge from '@/modules/common/components/Badge.tsx';
import { MeetingRecallDocuments } from '@/modules/common/components/MeetingRecallDocuments.tsx';
import { MeetingRemindersSummary } from '@/modules/common/components/MeetingRemindersSummary.tsx';
import {
  BadgeOnline,
  ButtonCopyLink,
  getMeetingLocalTimeSummary,
} from '@/modules/common/components/items/MeetingItem.tsx';
import { AddUpdateMeetingModal } from '@/modules/common/components/modals/AddUpdateMeetingModal.tsx';
import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';
import { utcIsoToMeetingDateTime } from '@/modules/common/helpers/meetingDateTime.ts';
import { normalizeMeetingReminder } from '@/modules/common/helpers/meetingReminders.ts';
import {
  useDeleteMeetingMutation,
  useGetMeetingQuery,
  useMeetingSummaryTemplatesQuery,
} from '@/modules/common/queries/meetings.ts';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import {
  Avatar,
  BaseModalProps,
  ModalContentWrapper,
  Spinner,
  TextWithShortLinks,
  getSelectValueByEnum,
} from 'ui';
import { fixNameEnum } from 'ui/utils';
import {
  MeetingAttendeeResponseStatus,
  MeetingRecurrencePatternType,
  MeetingRecurrenceRangeType,
  ScheduleMeetingRecurrence,
  ScheduledMeetingAttendeeResponse,
} from 'compliance-api';

interface MeetingAttendee extends ScheduledMeetingAttendeeResponse {
  id?: number;
  avatar_url?: string;
}

const responseStatusConfig = {
  [MeetingAttendeeResponseStatus.Accepted]: {
    label: 'Accepted',
    symbol: '✓',
    type: 'success',
    className: '!bg-green-50 !text-green-700',
  },
  [MeetingAttendeeResponseStatus.Declined]: {
    label: 'Declined',
    symbol: '✕',
    type: 'critical',
    className: '!bg-red-50 !text-red-700',
  },
  [MeetingAttendeeResponseStatus.Tentative]: {
    label: 'Tentative',
    symbol: '–',
    type: 'warning',
    className: '!bg-yellow-50 !text-yellow-700',
  },
  [MeetingAttendeeResponseStatus.Unknown]: {
    label: 'Unknown',
    symbol: '?',
    type: 'neutral',
    className: '!bg-gray-200 !text-gray-600',
  },
} satisfies Record<
  MeetingAttendeeResponseStatus,
  {
    label: string;
    symbol: string;
    type: 'success' | 'info' | 'warning' | 'critical' | 'neutral';
    className: string;
  }
>;

export interface MeetingItemFullInfoModalProps extends BaseModalProps {
  id: number;
}

export const MeetingItemFullInfoModal = ({ open, onClose, id }: MeetingItemFullInfoModalProps) => {
  const userRole = rolesManipulator.current_user_role;
  const isAdmin = userRole === Role.COMPLIANCE_ADMIN;
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const { data: meetingResponse, isLoading } = useGetMeetingQuery(
    { meetingId: id },
    { disabled: !open || !id },
  );
  const item = meetingResponse?.data;
  const isAiBotEnabled = !!item?.create_ai_bot;
  const shouldShowAttendeeResponseStatuses = !!item?.is_online_meeting;

  const { data: templatesResponse } = useMeetingSummaryTemplatesQuery({
    disabled: !open || !isAiBotEnabled,
  });
  const templateName = useMemo(() => {
    if (!item?.template_id) return 'General';

    return (
      templatesResponse?.data.find(template => template.id === item.template_id)?.name || 'General'
    );
  }, [item?.template_id, templatesResponse?.data]);

  const attendees = useMemo<MeetingAttendee[]>(() => {
    if (!item) return [];

    const usersById = new Map((item.attendee_users || []).map(user => [user.id, user]));
    const usersByEmail = new Map(
      (item.attendee_users || []).map(user => [user.email.toLowerCase(), user]),
    );

    if (item.attendee_responses?.length) {
      return item.attendee_responses.map(attendee => {
        const user =
          attendee.user_id !== undefined
            ? usersById.get(attendee.user_id)
            : usersByEmail.get(attendee.email.toLowerCase());

        return {
          ...attendee,
          id: attendee.user_id,
          avatar_url: user?.avatar_url,
        };
      });
    }

    const users = (item.attendee_users || []).map<MeetingAttendee>(user => ({
      attendee_type: 'user',
      user_id: user.id,
      id: user.id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      display_name: `${user.first_name} ${user.last_name}`.trim(),
      response_status: MeetingAttendeeResponseStatus.Unknown,
      avatar_url: user.avatar_url,
    }));

    const emails = item.external_attendee_emails.map<MeetingAttendee>(email => ({
      attendee_type: 'external',
      email: email,
      response_status: MeetingAttendeeResponseStatus.Unknown,
    }));

    return users.concat(emails);
  }, [item]);

  const reminders = useMemo(
    () => item?.reminders?.map(normalizeMeetingReminder) ?? [],
    [item?.reminders],
  );

  const localTimeSummary = useMemo(() => {
    if (!item) return '';

    return getMeetingLocalTimeSummary(item.start_time, item.end_time, { timeZone: item.time_zone });
  }, [item]);

  const timeZoneStart = useMemo(() => {
    if (!item) return null;

    return utcIsoToMeetingDateTime(item.start_time, item.time_zone);
  }, [item]);

  const timeZoneEnd = useMemo(() => {
    if (!item) return null;

    return utcIsoToMeetingDateTime(item.end_time, item.time_zone);
  }, [item]);

  const repeat = useMemo<string>(() => {
    const r = item?.recurrence as ScheduleMeetingRecurrence | null;
    if (!r) return 'Never';

    const interval = r.interval ?? 1;

    const fmtEnd = () => {
      if (r.range_type === MeetingRecurrenceRangeType.EndDate && r.end_date) {
        return ` until ${dayjs(r.end_date).format('DD MMM YYYY')}`;
      }
      if (r.range_type === MeetingRecurrenceRangeType.Numbered && r.number_of_occurrences) {
        return ` for ${r.number_of_occurrences} occurrences`;
      }
      return '';
    };

    switch (r.pattern_type) {
      case MeetingRecurrencePatternType.Daily:
        return interval === 1 ? 'Daily' + fmtEnd() : `Every ${interval} days` + fmtEnd();

      case MeetingRecurrencePatternType.Weekly: {
        const days = r.days_of_week?.map(d => fixNameEnum(d))?.join(', ') ?? '';
        return interval === 1
          ? `Weekly on ${days}` + fmtEnd()
          : `Every ${interval} weeks on ${days}` + fmtEnd();
      }

      case MeetingRecurrencePatternType.RelativeMonthly: {
        const day = r.day_of_month ? `on day ${r.day_of_month}` : '';
        return interval === 1
          ? `Monthly ${day}`.trim() + fmtEnd()
          : `Every ${interval} months ${day}`.trim() + fmtEnd();
      }

      case MeetingRecurrencePatternType.RelativeYearly: {
        const month = r.month
          ? dayjs()
              .month(r.month - 1)
              .format('MMMM')
          : '';
        const day = r.day_of_month || '';
        return interval === 1
          ? `Yearly on ${month} ${day}`.trim() + fmtEnd()
          : `Every ${interval} years on ${month} ${day}`.trim() + fmtEnd();
      }

      default:
        return 'Custom';
    }
  }, [item?.recurrence]);

  return (
    <ModalContentWrapper
      title={
        !!item && (
          <div className="flex flex-col gap-4">
            <BadgeOnline isOnline={item?.is_online_meeting} />
            <div className="font-[RecifeDisplay] font-semibold text-xl text-gray-900">
              {item?.subject}
            </div>
            <ButtonCopyLink link={item?.join_url} color={'primary'} />
          </div>
        )
      }
      variant="right"
      onClose={onClose}
      open={open}
      classes={{ children: 'p-0' }}
      onEdit={isAdmin && item ? () => setOpenEdit(true) : undefined}
      onDelete={isAdmin && item ? () => setOpenDelete(true) : undefined}
    >
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center py-10">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      {!isLoading && !item && (
        <div className="px-6 py-8 text-sm text-gray-500">Meeting not found.</div>
      )}
      {!isLoading && item && (
        <>
          <div className="flex flex-wrap py-8 px-6 border-b">
            <div className="leading-5 flex flex-col gap-2 w-1/2 pb-8">
              <span className="text-xs text-gray-500">Date</span>
              <span className="text-sm text-gray-900">{timeZoneStart?.format('DD-MM-YYYY')}</span>
            </div>

            <div className="leading-5 flex flex-col gap-2 w-1/2">
              <span className="text-xs text-gray-500">Start</span>
              <span className="text-sm text-gray-900">{timeZoneStart?.format('HH:mm')}</span>
            </div>

            <div className="leading-5 flex flex-col gap-2 w-1/2 pb-8">
              <span className="text-xs text-gray-500">End</span>
              <span className="text-sm text-gray-900">
                {timeZoneEnd?.isSame(timeZoneStart, 'day')
                  ? timeZoneEnd?.format('HH:mm')
                  : timeZoneEnd?.format('DD-MM-YYYY, HH:mm')}
              </span>
            </div>

            <div className="leading-5 flex flex-col gap-2 w-1/2">
              <span className="text-xs text-gray-500">Timezone</span>
              <span className="text-sm text-gray-900">
                {getSelectValueByEnum<Timezone>(item.time_zone, Timezone, false).label}
                {timeZoneStart ? ` (GMT${timeZoneStart.format('Z')})` : ''}
              </span>
            </div>

            {localTimeSummary && (
              <div className="leading-5 flex flex-col gap-2 w-full pb-8">
                <span className="text-xs text-gray-500">Local time</span>
                <span className="text-sm text-gray-900">{localTimeSummary}</span>
              </div>
            )}

            {item.location && (
              <div className="leading-5 flex flex-col gap-2 w-full pb-8">
                <span className="text-xs text-gray-500">Location</span>
                <span className="text-sm text-gray-900">{item.location}</span>
              </div>
            )}

            <div className="leading-5 flex flex-col gap-2 w-full pb-8">
              <span className="text-xs text-gray-500">Description</span>
              <span className="text-sm text-gray-900">
                {item.body ? <TextWithShortLinks text={item.body} /> : '...'}
              </span>
            </div>

            <div className="leading-5 flex flex-col gap-2 w-full pb-8">
              <span className="text-xs text-gray-500">Repeat</span>
              <span className="text-sm text-gray-900">{repeat}</span>
            </div>

            {isAiBotEnabled && (
              <div className="leading-5 flex flex-col gap-2 w-1/2 pb-8">
                <span className="text-xs text-gray-500">Meeting template</span>
                <span className="text-sm text-gray-900">{templateName}</span>
              </div>
            )}

            <div className="leading-5 flex flex-col gap-2 w-1/2">
              <span className="text-xs text-gray-500">Create AI bot</span>
              <span className="text-sm text-gray-900">
                {isAiBotEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {isAiBotEnabled && (
              <div className="leading-5 flex flex-col gap-2 w-full">
                <span className="text-xs text-gray-500">AI bot name</span>
                <span className="text-sm text-gray-900">{item.bot_name || 'Compli AI'}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-6 py-8 px-6 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm leading-[22px] text-gray-900 font-medium">Attendees</span>
              <span className="text-sm leading-[22px] text-gray-500 font-medium">
                {attendees?.length ?? 0}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {attendees?.map(attendee => {
                const status = responseStatusConfig[attendee.response_status];
                const attendeeName =
                  attendee.display_name ||
                  `${attendee.first_name ?? ''} ${attendee.last_name ?? ''}`.trim() ||
                  attendee.email;

                return (
                  <div
                    className="flex min-w-0 items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2"
                    key={attendee.id ?? attendee.email}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Avatar
                        src={attendee.avatar_url}
                        lastName={attendee.last_name ?? attendee.email}
                        firstName={attendee?.first_name}
                        size="sm"
                        className="border-[#C4C4C4]"
                        classNameLetters="text-[12px]"
                        withBorder={true}
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm leading-5 text-gray-900">
                          {attendeeName}
                        </span>
                        {attendeeName !== attendee.email && (
                          <span className="truncate text-xs leading-5 text-gray-500">
                            {attendee.email}
                          </span>
                        )}
                      </div>
                      {attendee.attendee_type === 'external' && (
                        <span className="text-sm leading-5 text-gray-500">(Guest)</span>
                      )}
                    </div>
                    {shouldShowAttendeeResponseStatuses && (
                      <Badge
                        type={status.type}
                        prefix={<span>{status.symbol}</span>}
                        className={classNames('shrink-0', status.className)}
                      >
                        {status.label}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <MeetingRemindersSummary reminders={reminders} withBottomBorder={isAiBotEnabled} />
          {isAiBotEnabled && <MeetingRecallDocuments meetingId={id} open={open} />}

          <DeleteModal
            {...{
              name: item.subject,
              id: item.id,
              title: 'meeting',
              mutation: useDeleteMeetingMutation,
            }}
            open={openDelete}
            onClose={() => setOpenDelete(false)}
            onSuccess={onClose}
          />
          {openEdit && !!item && (
            <AddUpdateMeetingModal
              open={openEdit}
              onClose={() => setOpenEdit(false)}
              item={item}
              skipOpenDetailsOnSuccess={true}
            />
          )}
        </>
      )}
    </ModalContentWrapper>
  );
};
